import React, { useRef, useState, useLayoutEffect, useMemo } from "react";
import { Component } from "@/types";
import { getChartSizeConfig, getChartResponsiveConfig } from "@/utils/chart-responsive-utils";
import { useDataTextValue } from "@/hooks/useDataTextValue";
import { useDataMultiValue, FieldConfig } from "@/hooks/useDataMultiValue";
import { useAnimatedValue } from "@/hooks/useAnimatedValue";
import { getLineKeysFromQuery } from "@/hooks/useQueryTemplateData";
import { ChartDataSource } from "@/types/chartDataSource";
import { resolveColorArray, resolveCSSVariable } from "@/utils/cssVariableResolver";
import { normalizeLegacyGaugeNeedleColor } from "@/utils/theme-aware-legacy-colors";
import { fullyDecodeHtmlEntities } from "@/utils/htmlEntities";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useComponentData } from "@/stores/editor/component-data-store";
import { DialRenderer } from "./gaugeRenderers/DialRenderer";
import { RingRenderer, ConcentricRingValue } from "./gaugeRenderers/RingRenderer";
import { BarRenderer, BarRendererProps, StackedSegment } from "./gaugeRenderers/BarRenderer";
import { MilestoneRenderer, MilestoneRendererProps } from "./gaugeRenderers/MilestoneRenderer";
import {
  defaultColors,
  VIEW_WIDTH, VIEW_HEIGHT, CX, CY, OUTER_R, INNER_R,
  RING_DEFAULT_THICKNESS, RING_DEFAULT_GAP,
  BAR_DEFAULT_HEIGHT, BAR_DEFAULT_CORNER_RADIUS,
  MILESTONE_DEFAULT_MARKER_SIZE,
  valueToAngle,
  buildThresholdSegments,
  getNeedleTriangles,
  getValueColorFromThresholds,
  GaugeThreshold,
  MilestoneItem,
} from "./gaugeRenderers/gaugeUtils";
import { ChartLoadingSkeleton } from "@/components/common/DataLoadingSkeletons";

// Re-export for backward compatibility
export type { GaugeThreshold } from "./gaugeRenderers/gaugeUtils";

interface GaugeChartProps {
  component: Component;
  isPreview?: boolean;
}

const toOptionalNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

export const GaugeChart: React.FC<GaugeChartProps> = ({
  component,
  isPreview = false,
}) => {
  const { properties = {}, style = {}, size } = component;
  const containerRef = useRef<HTMLDivElement>(null);

  // Display mode dispatcher
  const displayMode = (properties.displayMode as string) || 'dial';

  // Track palette changes via CSS variable mutations
  const [paletteChangeCounter, setPaletteChangeCounter] = useState(0);

  const dataSource = properties.dataSource as ChartDataSource | undefined;
  const templateId = properties.queryTemplateId as string | undefined;
  const valueField = (properties.valueKey as string)
    || (properties.valueField as string)
    || ((properties as Record<string, unknown>).lineKeys as string[] | undefined)?.[0]
    || (dataSource?.queryDefinition ? getLineKeysFromQuery(dataSource.queryDefinition)[0] : null)
    || "value";

  // Component data store (populated by fetch-schema-data interactions)
  const { data: storeData, loading: storeLoading, error: storeError } = useComponentData(component.id);

  const {
    value: rawValue,
    isLoading: loading,
    error,
    refetch: qtRefetch,
    hasDataSource: hasQueryTemplate,
    lastFetched,
  } = useDataTextValue({
    dataSource,
    componentId: component.id,
    valueField,
    staticValue: dataSource ? undefined : (properties.value as string | number | undefined),
    templateId,
  });

  // Determine if multi-value data binding is needed
  const needsMultiValue = (
    (displayMode === 'ring' && !!properties.concentricDataFields) ||
    (displayMode === 'bar' && !!properties.stackedDataFields) ||
    (displayMode === 'milestone')
  );
  const multiValueMode: 'field' | 'row' = displayMode === 'milestone' ? 'row' : 'field';

  // Always call useDataMultiValue (rules of hooks) — disabled when not needed
  const multiValueResult = useDataMultiValue({
    dataSource,
    componentId: component.id,
    mode: multiValueMode,
    fields: (properties.concentricDataFields || properties.stackedDataFields) as FieldConfig[] | undefined,
    nameField: (properties.milestoneNameField as string) || 'name',
    completedField: (properties.milestoneCompletedField as string) || 'completed',
    enabled: needsMultiValue,
    templateId,
  });

  // Prefer store data value over hook value (store data comes from fetch-schema-data interactions)
  const storeValue = storeData.length > 0 ? (storeData[0] as Record<string, unknown>)?.[valueField] : undefined;
  const effectiveRawValue = storeValue !== undefined && storeValue !== null ? storeValue : rawValue;
  const hasValue = effectiveRawValue !== null && effectiveRawValue !== undefined;
  const value = hasValue
    ? (typeof effectiveRawValue === 'number' ? effectiveRawValue : parseFloat(String(effectiveRawValue)) || 0)
    : (properties.min as number) ?? 0;
  const minValue = (properties.min as number) ?? 0;
  const maxValue = (properties.max as number) ?? 100;
  const gaugeValue = Math.min(Math.max(value, minValue), maxValue);

  const displayValue = Number.isInteger(value) ? value : parseFloat(value.toFixed(2));

  // Animate gauge value — disabled for milestone mode and preview
  const animateEnabled = (properties.animateValue as boolean) !== false && displayMode !== 'milestone' && !isPreview;
  const animatedValue = useAnimatedValue(gaugeValue, 300, animateEnabled);

  const sizeConfig = getChartSizeConfig(
    toOptionalNumber(size?.width),
    toOptionalNumber(size?.height),
  );
  const title = properties.title === undefined ? "Gauge Chart" : (properties.title as string);
  const description = properties.description as string;
  const label = (properties.label as string) || "Value";
  const unit = fullyDecodeHtmlEntities((properties.unit as string) || "");

  const responsiveConfig = getChartResponsiveConfig(sizeConfig, !!title, !!description, undefined, false, false);

  // ARIA label for renderers
  const gaugeAriaLabel = `${label || 'Gauge'}: ${hasValue ? displayValue : 'no data'}${unit ? ' ' + unit : ''}`;

  // Keyboard focus for interactive gauges
  const hasClickInteraction = (component.interactions ?? []).some(
    i => i.enabled && (i.trigger.type === 'onClick' || i.trigger.type === 'onDoubleClick')
  );

  const [resolvedColors, setResolvedColors] = useState<string[]>(() => (properties.colors as string[]) || defaultColors);

  // Watch for CSS variable changes on ancestor with data-palette-scope
  useLayoutEffect(() => {
    const findPaletteScope = (element: HTMLElement | null): HTMLElement | null => {
      while (element) {
        if (element.dataset.paletteScope === 'true') return element;
        element = element.parentElement;
      }
      return null;
    };

    const paletteScope = findPaletteScope(containerRef.current);
    if (!paletteScope) return;

    const observer = new MutationObserver(() => {
      setPaletteChangeCounter(c => c + 1);
    });

    observer.observe(paletteScope, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const colors = (properties.colors as string[]) || defaultColors;
    const resolved = resolveColorArray(colors, containerRef.current);
    setResolvedColors(resolved);
  }, [properties.colors, component.id, paletteChangeCounter]);

  const [resolvedThresholds, setResolvedThresholds] = useState<GaugeThreshold[]>([]);
  useLayoutEffect(() => {
    const thresholds = (properties.thresholds as GaugeThreshold[]) || [];
    if (thresholds.length === 0) {
      setResolvedThresholds([]);
      return;
    }
    const resolved = thresholds.map(t => ({
      ...t,
      color: resolveCSSVariable(t.color, containerRef.current),
    }));
    setResolvedThresholds(resolved);
  }, [properties.thresholds, component.id, paletteChangeCounter]);

  const hasThresholds = resolvedThresholds.length > 0;
  const segmentGap = (properties.segmentGap as number) || 0;
  const roundedSegments = (properties.roundedSegments as boolean) || false;
  const segmentOpacity = (properties.segmentOpacity as number) ?? 1;
  const ARC_START = 175; // Inset so rounded ends don't cover min/max labels
  const ARC_END = 5;

  const arcSegments = useMemo(() => {
    if (displayMode === 'milestone') return [];
    if (hasThresholds && resolvedThresholds.length > 0) {
      const gapColor = resolvedColors[1] || defaultColors[1];
      const effectiveGap = roundedSegments ? segmentGap * 0.3 : segmentGap;
      return buildThresholdSegments(resolvedThresholds, minValue, maxValue, gapColor, effectiveGap, ARC_START, ARC_END);
    }
    const valueAngle = Math.min(Math.max(valueToAngle(animatedValue, minValue, maxValue), ARC_END), ARC_START);
    return [
      { startAngle: ARC_START, endAngle: valueAngle, color: resolvedColors[0] || defaultColors[0], label: `${label || 'Value'}: ${displayValue}${unit}` },
      { startAngle: valueAngle, endAngle: ARC_END, color: resolvedColors[1] || defaultColors[1], label: `Remaining: ${Math.round(maxValue - animatedValue)}${unit}` },
    ];
  }, [displayMode, hasThresholds, resolvedThresholds, minValue, maxValue, animatedValue, resolvedColors, segmentGap, roundedSegments, label, displayValue, unit]);

  const needleTriangles = useMemo(() => {
    if (displayMode === 'milestone') return { outerPath: '', innerPath: '' };
    const angle = Math.min(Math.max(valueToAngle(animatedValue, minValue, maxValue), ARC_END), ARC_START);
    return getNeedleTriangles(angle, CX, CY, INNER_R, OUTER_R);
  }, [displayMode, animatedValue, minValue, maxValue]);

  const { width: _w, minWidth: _mw, maxWidth: _xw, ...visualStyle } = style as React.CSSProperties;
  const containerStyle: React.CSSProperties = {
    ...visualStyle,
    position: 'relative',
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  };

  const needleColor = useMemo(() => {
    const color = normalizeLegacyGaugeNeedleColor(
      (properties.needleColor as string) || "hsl(220 10% 48%)",
    );
    return resolveCSSVariable(color, containerRef.current || undefined);
  }, [properties.needleColor, paletteChangeCounter]);

  const defaultValueColor = useMemo(() => {
    const color = (properties.valueColor as string) || "#1f2937";
    return resolveCSSVariable(color, containerRef.current || undefined);
  }, [properties.valueColor, paletteChangeCounter]);

  // Dynamic value color: use threshold zone color when thresholds are active
  // Use animatedValue so color follows the animated position through threshold zones
  const displayValueColor = hasThresholds
    ? getValueColorFromThresholds(animatedValue, resolvedThresholds, defaultValueColor)
    : defaultValueColor;

  // Merge loading/error states from all data sources (store + single-value hook + multi-value hook)
  const isMultiLoading = needsMultiValue && multiValueResult.isLoading;
  const multiError = needsMultiValue ? multiValueResult.error : null;
  const showLoading = storeLoading || loading || isMultiLoading;
  const showError = storeError || error || multiError;

  // Effective milestones: data-bound or static
  const effectiveMilestones = multiValueResult.hasDataSource && multiValueResult.milestones.length > 0
    ? multiValueResult.milestones.map(m => ({ id: m.id, label: m.name, completed: m.completed }))
    : (properties.milestones as MilestoneItem[]) || [];

  const hasData = displayMode === 'milestone'
    ? effectiveMilestones.length > 0
    : arcSegments.length > 0;

  // Build dial renderer props
  const dialProps = {
    viewWidth: VIEW_WIDTH, viewHeight: VIEW_HEIGHT,
    cx: CX, cy: CY, outerR: OUTER_R, innerR: INNER_R,
    value: animatedValue, displayValue, min: minValue, max: maxValue, hasValue,
    arcSegments, needleOuterPath: needleTriangles.outerPath, needleInnerPath: needleTriangles.innerPath,
    resolvedColors, resolvedThresholds, needleColor, valueColor: displayValueColor,
    segmentGap, roundedSegments, segmentOpacity, showRangeLabels: !!properties.showRangeLabels,
    label, unit,
    valueFontSize: (properties.valueFontSize as number) || 32,
    labelFontSize: (properties.labelFontSize as number) || 14,
    ariaLabel: gaugeAriaLabel,
  };

  // Build ring renderer props — use data-bound concentric values when available
  const ringProps = {
    value: animatedValue,
    displayValue,
    min: minValue,
    max: maxValue,
    hasValue,
    resolvedColors,
    resolvedThresholds,
    valueColor: displayValueColor,
    ringThickness: (properties.ringThickness as number) || RING_DEFAULT_THICKNESS,
    startAngle: (properties.startAngle as number) || 90,
    concentricValues: multiValueResult.hasDataSource && multiValueResult.values.length > 0
      ? multiValueResult.values.map(v => ({ value: v.value, max: v.max, color: v.color, label: v.label }))
      : (properties.concentricValues as ConcentricRingValue[] | undefined),
    ringGap: (properties.ringGap as number) || RING_DEFAULT_GAP,
    label,
    unit,
    valueFormat: (properties.valueFormat as string) || '{value}',
    showValue: (properties.showValue as boolean) ?? true,
    showLabel: (properties.showLabel as boolean) ?? true,
    valueFontSize: (properties.valueFontSize as number) || 24,
    labelFontSize: (properties.labelFontSize as number) || 12,
    segmentGap,
    roundedSegments,
    segmentOpacity,
  };

  // Build bar renderer props — use data-bound stacked segments when available
  const barProps: BarRendererProps = {
    value: animatedValue,
    displayValue,
    min: minValue,
    max: maxValue,
    hasValue,
    resolvedColors,
    resolvedThresholds,
    valueColor: displayValueColor,
    barHeight: (properties.barHeight as number) || BAR_DEFAULT_HEIGHT,
    barCornerRadius: (properties.barCornerRadius as number) ?? BAR_DEFAULT_CORNER_RADIUS,
    stackedSegments: multiValueResult.hasDataSource && multiValueResult.values.length > 0
      ? multiValueResult.values.map(v => ({ value: v.value, color: v.color, label: v.label }))
      : (properties.stackedSegments as StackedSegment[] | undefined),
    showSegmentLabels: (properties.showSegmentLabels as boolean) ?? false,
    showThresholdMarkers: (properties.showThresholdMarkers as boolean) ?? false,
    label,
    unit,
    valueFormat: (properties.valueFormat as string) || '{value}',
    showValue: (properties.showValue as boolean) ?? true,
    showLabel: (properties.showLabel as boolean) ?? true,
    valuePosition: (properties.valuePosition as 'right' | 'above' | 'below') || 'right',
    valueFontSize: (properties.valueFontSize as number) || 14,
    labelFontSize: (properties.labelFontSize as number) || 11,
    segmentOpacity,
    componentId: component.id,
  };

  // Build milestone renderer props — use data-bound milestones when available
  const milestoneProps: MilestoneRendererProps = {
    milestones: effectiveMilestones,
    completedColor: resolvedColors[0] || defaultColors[0],
    trackColor: resolvedColors[1] || defaultColors[1],
    markerStyle: (properties.markerStyle as 'circle' | 'diamond' | 'square') || 'circle',
    markerSize: (properties.markerSize as number) || MILESTONE_DEFAULT_MARKER_SIZE,
    showLabels: (properties.showLabels as boolean) ?? true,
    label: label || '',
    showSummary: (properties.showSummary as boolean) ?? true,
    labelFontSize: (properties.labelFontSize as number) || 10,
    summaryFontSize: (properties.summaryFontSize as number) || 9,
  };

  // Mode dispatcher
  const renderGauge = () => {
    switch (displayMode) {
      case 'ring':
        return <RingRenderer {...ringProps} />;
      case 'bar':
        return <BarRenderer {...barProps} />;
      case 'milestone':
        return <MilestoneRenderer {...milestoneProps} />;
      case 'dial':
      default:
        return <DialRenderer {...dialProps} />;
    }
  };

  return (
    <div
      className="w-full h-full"
      style={containerStyle}
      ref={containerRef}
      data-testid="gauge-chart"
      tabIndex={hasClickInteraction ? 0 : undefined}
      onFocus={hasClickInteraction ? (e) => {
        e.currentTarget.style.outline = '2px solid #3b82f6';
        e.currentTarget.style.outlineOffset = '2px';
        e.currentTarget.style.borderRadius = '4px';
      } : undefined}
      onBlur={hasClickInteraction ? (e) => {
        e.currentTarget.style.outline = '';
        e.currentTarget.style.outlineOffset = '';
        e.currentTarget.style.borderRadius = '';
      } : undefined}
    >
      {responsiveConfig.showTitle && (
        <div className={`text-center ${responsiveConfig.titleMargin} relative`}>
          <h3 className={responsiveConfig.titleClass}>{title}</h3>
          {responsiveConfig.showDescription && (
            <p className="text-xs text-muted-foreground">
              {description}
            </p>
          )}
          {hasQueryTemplate && !isPreview && (
            <button
              onClick={() => qtRefetch()}
              className="absolute right-2 top-0 p-1 rounded hover:bg-muted/50 transition-colors"
              title={lastFetched ? `Last updated: ${lastFetched.toLocaleTimeString()}` : 'Refresh data'}
              disabled={showLoading}
            >
              <RefreshCw className={`w-4 h-4 text-muted-foreground ${showLoading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      )}

      <div style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {showLoading ? (
          <ChartLoadingSkeleton variant="gauge" testId="gauge-loading" />
        ) : showError ? (
          <div className="flex flex-col items-center justify-center h-full space-y-2" data-testid="gauge-error">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-sm text-red-600">{showError}</p>
          </div>
        ) : hasData ? (
          renderGauge()
        ) : (
          <div className="flex items-center justify-center h-full" data-testid="gauge-no-data">
            <p className="text-muted-foreground">No chart data available</p>
          </div>
        )}
      </div>
    </div>
  );
};
